import { useEffect, useRef, useState } from "react";
import "../../../styles/homePage/addPatient.scss";
import AddPatientForm from "./AddPatientForm";
import {
  ClinicalInfoInterface,
  DemographicInfoInterface,
} from "../../../types/PatientData.interface";
import infoIcon from "../../../common/assats/icons/infoIcon.svg";
import addressIcon from "../../../common/assats/icons/addressIcon.svg";
import clinicalIcon from "../../../common/assats/icons/clinicalIcon.svg";
import {
  createPatient,
  createPatientClinical,
  getPatient,
  getPatientClinical,
  updatePatient,
  updatePatientClinical,
} from "../../../common/api/apiCalls";
import patientDataTemplate from "./patientDataTemplate";
import DynamicModal from "../../common/Modal/DynamicModal";
import { ReactComponent as DisqualifiedIcon } from "../../../common/assats/icons/disqualifiedIcon.svg";
import getValidationSchemas, {
  StepsEnum,
} from "../../../common/validations/ValidationSchemas";
import useFormValidation from "../../../common/hooks/UseFormValidation";
import { useLocation, useNavigate } from "react-router-dom";
import MainHome from "../../MainHome";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../common/contexts/useToast";
import { NavigationPaths } from "../../../common/consts/navigationPaths";
import useUserStore from "../../../common/store/user.store";
import { cmToFeetAndInches, kgToLbs } from "../../../common/utils/hashUtil";

const usePrevious = (
  value: ClinicalInfoInterface & DemographicInfoInterface
) => {
  const ref = useRef<
    (ClinicalInfoInterface & DemographicInfoInterface) | undefined
  >(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
const AddPatient = () => {
  const { user } = useUserStore();
  const location = useLocation();
  const [step, setStep] = useState<StepsEnum>(StepsEnum.DEMOGRAPHICS);
  const [showErrors, setShowErrors] = useState(true);
  const [patientDisqualified, setPatientDisqualified] = useState(false);
  const [isEdit, setIsEdit] = useState<boolean>(location.state?.edit || false);
  const locationState = location.state?.data || null;
  const patientId = locationState?.patientId;
  const identityNumber = locationState?.identityNumber;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [hasClinical, setHasClinical] = useState(false);

  const isNonClinicalUser = user && user.role.includes("NonClinicalUser");

  const lastStep = isNonClinicalUser ? StepsEnum.ADDRESS : StepsEnum.LAST_STEP;


  const [patientData, setPatientData] = useState<
    ClinicalInfoInterface & DemographicInfoInterface
  >({
    ...patientDataTemplate,
  });

  const { checkForErrors, errors, touched, setTouched } = useFormValidation({
    formValues: patientData,
    validationSchema: getValidationSchemas(patientData)[step],
  });

  useEffect(() => {
    isEdit && getPatientData();
  }, [patientId, isEdit]);

  const getPatientData = async () => {

    const patientData = await getPatient(identityNumber!);
    const patientUid = patientData.id;
    const metrics = user?.metrics;

    const res = await getPatientClinical(patientUid!);
    const clinicalData = res.data.clinicalData;
    setHasClinical(!!(clinicalData && Object.keys(clinicalData).length > 0));
    const chestPainType = clinicalData?.chestPainType || null;
    const combinedData = {
      patientId: patientUid,
      ...patientData,
      ...clinicalData,
      chestPainType,
      metrics,
    };
    // if (metrics === 'imperial') {
    //   const heightCm = Number(combinedData.height);
    //   const weightKg = Number(combinedData.weight);

    //   // Convert height from cm to feet.inches
    //   const totalInches = heightCm / 2.54;
    //   const feet = Math.floor(totalInches / 12);
    //   const inches = Math.round(totalInches % 12);
    //   combinedData.height = `${feet}.${inches}`; // example: "5.11"

    //   // Convert weight from kg to lbs
    //   const weightInLbs = Math.round(weightKg / 0.453592);
    //   combinedData.weight = Math.round(weightInLbs); // "176.37"
    // }
    combinedData.heightInch = cmToFeetAndInches(combinedData.height).inches.toString();
    combinedData.heightFeet = cmToFeetAndInches(combinedData.height).feet.toString();
    combinedData.weightLbs = kgToLbs(combinedData.weight).toString();
    
    setPatientData(combinedData);
  };

  useEffect(() => {
    if (locationState && locationState?.identityNumber) {
      setIsEdit(true);
      const metrics = user?.metrics;
      setPatientData({ ...locationState, metrics });
    } else {
      if (user?.metrics) {
        setPatientData({ ...patientData, metrics: user?.metrics });
      }
    }
  }, [locationState, user?.metrics]);

  const forwardStep = async () => {
    const test = await checkForErrors({ checkAll: true });
    if (test) {
      showToast("error", "Error", t("general.errors.fillAllFields"));
      return;
    }

    setStep(step + 1);
  };


  const isPatientDisqualificationData = () => {
    return (
      patientData.arrhythmiasOrConductionAbnormality ||
      patientData.strokeOrTiaOrRIND ||
      patientData.chronicHeartFailure ||
      patientData.lvefLessThan30 ||
      patientData.previousCardiacEvent ||
      patientData.hemodynamicInstability ||
      patientData.pulmonaryEdema ||
      patientData.anemia
    );
  }

  useEffect(() => {
    if (isPatientDisqualificationData()) {
      setPatientDisqualified(true);
    }
  }, [patientData.arrhythmiasOrConductionAbnormality ||
    patientData.strokeOrTiaOrRIND ||
    patientData.chronicHeartFailure ||
    patientData.lvefLessThan30 ||
    patientData.previousCardiacEvent ||
    patientData.hemodynamicInstability ||
    patientData.pulmonaryEdema ||
    patientData.anemia])

  const backStep = () => {
    setStep(step - 1);
  };

  const submitForm = async () => {
    if (await checkForErrors({ checkAll: true })) {
      showToast("error", "Error", t("general.errors.fillAllFields"));
      return;
    }

    if (isPatientDisqualificationData()) {
      setPatientDisqualified(true);
      return;
    }

    const patientDataObj = {
      patientId: locationState?.patientId || undefined,
      honorific: patientData.honorific || undefined,
      firstName: patientData.firstName,
      middleName: patientData.middleName,
      identityNumber: patientData.identityNumber,
      lastName: patientData.lastName,
      birthday: patientData.birthday,
      gender: patientData.gender,
      phoneNumber: patientData.phoneNumber,
      email: patientData.email,
      state: patientData.state,
      address: patientData.address,
      city: patientData.city,
      country: patientData.country,
      zip: patientData.zip,
      maritalStatus: patientData.maritalStatus,
      race: patientData.race,
    };

    let createPatientResponse = null;
    if (isEdit) {
      createPatientResponse = await updatePatient(
        patientDataObj,
        locationState.identityNumber
      );
    } else {
      createPatientResponse = await createPatient(patientDataObj);
    }

    if (!createPatientResponse?.success) {
      showToast("error", "Error", t(createPatientResponse.data.errorMessage));
      return;
    }
    if (isNonClinicalUser) {
      handleClose();
      return;
    }
    // if (patientData && patientData.metrics === 'imperial') {
    //   // Height: "5.11" -> см
    //   //if (typeof patientData.height === 'string') {
    //   const heightFloat = Number(patientData.height);
    //   const feet = Math.floor(heightFloat);
    //   const inches = Math.round((heightFloat - feet) * 100);
    //   const heightInCm = (feet * 12 + inches) * 2.54;
    //   patientData.height = Number(heightInCm.toFixed(2)).toString();
    //   patientData.heightFeet = cmToFeetAndInches(patientData.height).feet.toString();
    //   patientData.heightInch = cmToFeetAndInches(patientData.height).inches.toString();
    //   //}

    //   // Weight: "180" -> кг
    //   //if (typeof patientData.weight === 'string') {
    //   const weightFloat = patientData.weight;
    //   const weightInKg = Number(weightFloat) * 0.453592;
    //   patientData.weight = Number(weightInKg.toFixed(2)).toString();
    //   // }
    // }


    const clinicalInfoData = {
      patientId:
        locationState?.patientId || createPatientResponse?.data?.patient?.id,
      height: parseFloat(patientData?.height || "") || undefined,
      weight: parseFloat(patientData.weight || "") || undefined,
      totalCholesterol:
        parseFloat(patientData.totalCholesterol || "") || undefined,
      ldl: parseFloat(patientData.ldl || "") || undefined,
      triglycerides: parseFloat(patientData.triglycerides || "") || undefined,
      bpSystolic: parseInt(patientData.bpSystolic || "", 10) || undefined,
      bpDiastolic: parseInt(patientData.bpDiastolic || "", 10) || undefined,
      sugarLevel: parseFloat(patientData.sugarLevel || "") || undefined,
      isSymptomatic: patientData.isSymptomatic,
      symptomsType: patientData?.symptomsType || null,
      chestPain: patientData?.chestPain,
      chestPainType: patientData?.chestPainType || null,
      shortnessOfBreath: patientData?.shortnessOfBreath,
      shoulderOrArmPain: patientData?.shoulderOrArmPain,
      jawPain: patientData?.jawPain,
      dizzinessOrPresyncope: patientData?.dizzinessOrPresyncope,
      nausea: patientData?.nausea,
      smokingStatus: patientData?.smokingStatus || undefined,
      takingOtherDrugs: patientData?.takingOtherDrugs || undefined,
      copd: patientData?.copd,
      arrhythmiasOrConductionAbnormality:
        patientData?.arrhythmiasOrConductionAbnormality,
      peripheralArterialDisease: patientData?.peripheralArterialDisease,
      familyHistoryCardiovascularEvents:
        patientData?.familyHistoryCardiovascularEvents,
      cerebrovascularDisease: patientData?.cerebrovascularDisease,
      strokeOrTiaOrRIND: patientData?.strokeOrTiaOrRIND,
      chronicHeartFailure: patientData?.chronicHeartFailure,
      lvefLessThan30: patientData?.lvefLessThan30,
      previousCardiacEvent: patientData?.previousCardiacEvent,
      hemodynamicInstability: patientData?.hemodynamicInstability,
      pulmonaryEdema: patientData?.pulmonaryEdema,
      anemia: patientData?.anemia,
      notes: patientData?.notes || undefined,
      otherCoMorbidities: patientData?.otherCoMorbidities || undefined,
    };
    let patientClinicalInfo = null;
    if (isEdit && hasClinical) {
      patientClinicalInfo = await updatePatientClinical(
        { ...clinicalInfoData },
        locationState.patientId
      );
    } else {
      patientClinicalInfo = await createPatientClinical(clinicalInfoData);
    }
    if (!patientClinicalInfo?.success) {
      showToast("error", "Error", t(patientClinicalInfo.data.errorMessage));
      return;
    }

    showToast(
      "success",
      "Success",
      isEdit
        ? t("patientsList.patientUpdateSuccess")
        : t("patientsList.patientCreateSuccess")
    );
    handleClose();
  };

  const renderStepIcon = () => {
    switch (step) {
      case StepsEnum.DEMOGRAPHICS:
        return infoIcon;
      case StepsEnum.ADDRESS:
        return addressIcon;
      default:
        return clinicalIcon;
    }
  };

  const handleClose = () => {
    navigate(NavigationPaths.PATIENTS);
  };

  const renderStepText = () => {
    switch (step) {
      case StepsEnum.DEMOGRAPHICS:
        return t("patientsList.personalInformation.title");
      case StepsEnum.ADDRESS:
        return t("patientsList.address.title");
      case StepsEnum.CLINICAL_ONE:
      case StepsEnum.CLINICAL_TWO:
      case StepsEnum.CLINICAL_THREE:
      case StepsEnum.LAST_STEP:
        return t("patientsList.clinicalInformation.title");
      default:
        return t("patientsList.demographicInformation.title");
    }
  };

  return (
    <MainHome>
      <div className="add-patient-container">
        <div className="add-patient-inner-container">
          <div className="add-patient-header">
            {isEdit
              ? t("patientsList.editPatient")
              : t("patientsList.addNewPatient")}
          </div>
          <div className="custom-divider" />
          <div className="add-patient-stage-title">
            <div className="stage-icon">
              <img src={renderStepIcon()} alt="Step Icon" />
            </div>
            <div className="stage-title">{renderStepText()}</div>
          </div>
          <AddPatientForm
            showErrors={showErrors}
            errors={errors}
            checkForErrors={({ checkAll, name }) =>
              checkForErrors({ checkAll, name })
            }
            currentStep={step}
            setPatientData={setPatientData}
            patientData={patientData}
            touched={touched}
            setTouched={setTouched}
          />
        </div>
        <hr className="form-footer-divider" />
        <div className="add-patient-button-group form-footer">
          {step < 1 ? (
            <button
              className="secondary-button"
              onClick={() => {
                handleClose();
              }}
            >
              {t("general.buttons.cancel")}
            </button>
          ) : (
            <button className="secondary-button" onClick={backStep}>
              {t("general.buttons.back")}
            </button>
          )}

          <button
            className="primary-button"
            onClick={step === lastStep ? submitForm : forwardStep}
          >
            {step === lastStep
              ? t("general.buttons.finish")
              : t("general.buttons.next")}
          </button>
        </div>
        {patientDisqualified && (
          <DynamicModal
            title={"Warning"}
            icon={<DisqualifiedIcon />}
            subtext={t("patientsList.errors.disqualificationMessage")}
            buttonText={"OK"}
            onButtonClick={() => setPatientDisqualified(false)}
            onClose={() => setPatientDisqualified(false)}
          />
        )}
      </div>
    </MainHome>
  );
};

export default AddPatient;
